import React, { useContext, useEffect, useState, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalBody,
  DropdownItem,
  Badge,
  Spinner,
} from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  PaginationComponent,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  RSelect,
} from "../../../components/Component";
import { useHistory, useLocation } from "react-router-dom";
import { ObservationContext } from "./ObservationContext";
import ObservationWizardModal from "./ObservationWizardModal";
import ObservationViewModal from "./ObservationViewModal";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const ObservationListPage = () => {
  const { contextData, loading, loadObservations } = useContext(ObservationContext);
  const [data, setData] = contextData;

  const [sm, updateSm] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteTitle, setDeleteTitle] = useState("");
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    severity: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [sites, setSites] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardObservationId, setWizardObservationId] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewObservationId, setViewObservationId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const isManualRefresh = useRef(false);
  const exportObservationsRef = useRef(() => {});
  const location = useLocation();
  const history = useHistory();

  // Charger les sites
  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      // Récupérer les sites depuis l'utilisateur connecté
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.sites && user.sites.length > 0) {
          setSites(user.sites);
          return;
        }
      }
      // Si pas de sites dans le profil, essayer de récupérer via API
      try {
        const response = await api.getSites();
        setSites(response.data || response || []);
      } catch (error) {
        // En cas d'erreur, essayer de récupérer depuis l'API /me
        try {
          const meResponse = await api.me();
          if (meResponse.sites && meResponse.sites.length > 0) {
            setSites(meResponse.sites);
          }
        } catch (meError) {
          toast.error("Erreur lors du chargement des sites");
        }
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des sites");
    }
  };

  // Charger les observations avec filtres
  useEffect(() => {
    // Ne pas recharger si c'est un rafraîchissement manuel
    if (!isManualRefresh.current) {
      loadObservations(filters);
    } else {
      isManualRefresh.current = false;
    }
  }, [filters]);

  const getTypeBadge = (type) => {
    if (!type || type === null || type === undefined || type === '') {
      return <span className="badge badge-dim bg-outline-secondary">Non défini</span>;
    }
    const typeLower = String(type).toLowerCase().trim();
    const badgeMap = {
      positive: { color: "success", label: "Positive" },
      dangerous: { color: "danger", label: "Dangereuse" },
      near_miss: { color: "warning", label: "Presqu'accident" },
      'near miss': { color: "warning", label: "Presqu'accident" },
      non_conformity: { color: "info", label: "Non-conformité" },
      'non-conformity': { color: "info", label: "Non-conformité" },
    };
    const badge = badgeMap[typeLower] || { color: "secondary", label: String(type) };
    return <span className={`badge text-bg-${badge.color}`}>{badge.label}</span>;
  };

  const getSeverityBadge = (severity) => {
    if (!severity || severity === null || severity === undefined || severity === '') {
      return <span className="badge badge-dim bg-outline-secondary">Non défini</span>;
    }
    const severityLower = String(severity).toLowerCase().trim();
    const badgeMap = {
      low: { color: "success", label: "Faible" },
      medium: { color: "warning", label: "Moyenne" },
      high: { color: "danger", label: "Élevée" },
    };
    const badge = badgeMap[severityLower] || { color: "secondary", label: String(severity) };
    return <span className={`badge text-bg-${badge.color}`}>{badge.label}</span>;
  };

  const getStatusBadge = (status) => {
    if (!status || status === null || status === undefined || status === '') {
      return <span className="badge badge-dim bg-outline-secondary">Non défini</span>;
    }
    const statusLower = String(status).toLowerCase().trim();
    const badgeMap = {
      submitted: { color: "secondary", label: "Soumise" },
      received: { color: "info", label: "Reçue" },
      in_analysis: { color: "warning", label: "En analyse" },
      'in analysis': { color: "warning", label: "En analyse" },
      corrective_action_requested: { color: "danger", label: "Action requise" },
      'corrective action requested': { color: "danger", label: "Action requise" },
      action_completed: { color: "success", label: "Action réalisée" },
      'action completed': { color: "success", label: "Action réalisée" },
      verified: { color: "primary", label: "Vérifiée" },
      closed: { color: "dark", label: "Clôturée" },
    };
    const badge = badgeMap[statusLower] || { color: "secondary", label: String(status) };
    return <span className={`badge text-bg-${badge.color}`}>{badge.label}</span>;
  };


  const onEditClick = (id) => {
    setWizardObservationId(id);
    setWizardOpen(true);
  };

  const openNewObservationWizard = () => {
    setWizardObservationId(null);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setWizardObservationId(null);
  };

  const onWizardSuccess = async () => {
    isManualRefresh.current = true;
    await loadObservations(filters);
  };

  const onViewClick = (id) => {
    setViewObservationId(id);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewObservationId(null);
  };

  const onDeleteClick = (id) => {
    const observation = data.find((obs) => obs.id === id);
    if (observation) {
      setDeleteId(id);
      setDeleteTitle(observation.title || "cette observation");
      setDeleteModal(true);
    }
  };

  const onDeleteConfirm = async () => {
    if (!deleteId) return;
    
    try {
      await api.deleteObservation(deleteId);
      toast.success("Observation supprimée avec succès");
      isManualRefresh.current = true;
      await loadObservations(filters);
      setDeleteModal(false);
      setDeleteId(null);
      setDeleteTitle("");
    } catch (error) {
      const errorMessage = error.message || "Erreur lors de la suppression de l'observation";
      toast.error(errorMessage);
    }
  };

  const onDeleteCancel = () => {
    setDeleteModal(false);
    setDeleteId(null);
    setDeleteTitle("");
  };

  const selectorCheck = (e) => {
    const newData = data.map((item) => {
      item.checked = e.currentTarget.checked;
      return item;
    });
    setData([...newData]);
  };

  const onSelectChange = (e, id) => {
    const newData = data;
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].checked = e.currentTarget.checked;
      setData([...newData]);
    }
  };

  // Filtrer les observations selon la recherche
  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      (item.title && item.title.toLowerCase().includes(searchLower)) ||
      (item.description && item.description.toLowerCase().includes(searchLower)) ||
      (item.site?.name && item.site.name.toLowerCase().includes(searchLower)) ||
      (item.creator?.first_name && item.creator.first_name.toLowerCase().includes(searchLower)) ||
      (item.creator?.last_name && item.creator.last_name.toLowerCase().includes(searchLower))
    );
  });

  const formatTypeLabel = (type) => {
    if (!type) return "";
    const key = String(type).toLowerCase().trim();
    const map = {
      positive: "Positive",
      dangerous: "Dangereuse",
      near_miss: "Presqu'accident",
      "near miss": "Presqu'accident",
      non_conformity: "Non-conformité",
      "non-conformity": "Non-conformité",
    };
    return map[key] || String(type);
  };

  const formatSeverityLabel = (severity) => {
    if (!severity) return "";
    const key = String(severity).toLowerCase().trim();
    const map = { low: "Faible", medium: "Moyenne", high: "Élevée" };
    return map[key] || String(severity);
  };

  const formatStatusLabel = (status) => {
    if (!status) return "";
    const key = String(status).toLowerCase().trim();
    const map = {
      submitted: "Soumise",
      received: "Reçue",
      in_analysis: "En analyse",
      "in analysis": "En analyse",
      corrective_action_requested: "Action requise",
      "corrective action requested": "Action requise",
      action_completed: "Action réalisée",
      "action completed": "Action réalisée",
      verified: "Vérifiée",
      closed: "Clôturée",
    };
    return map[key] || String(status);
  };

  const exportObservationsToExcel = async () => {
    if (!filteredData.length) {
      toast.warning("Aucune observation à exporter");
      return;
    }
    if (exportLoading) return;

    setExportLoading(true);
    try {
      const [{ downloadObservationsExcelStyled }, { observationsToExportRows, OBSERVATION_EXPORT_COLUMNS }] =
        await Promise.all([
          import("../../../utils/exportObservationsExcelStyled"),
          import("../../../utils/observationExportFormatters"),
        ]);

      const fullObservations = await Promise.all(
        filteredData.map(async (item) => {
          try {
            const res = await api.getObservation(item.id);
            return res.data || res;
          } catch {
            return item;
          }
        })
      );

      const dataRows = observationsToExportRows(fullObservations);
      const headers = OBSERVATION_EXPORT_COLUMNS.map((c) => c.header);
      const widths = OBSERVATION_EXPORT_COLUMNS.map((c) => c.width);

      await downloadObservationsExcelStyled({
        headers,
        dataRows,
        widths,
        total: fullObservations.length,
        generatedAt: new Date(),
      });

      toast.success("Export Excel téléchargé");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Impossible de générer l'export Excel");
    } finally {
      setExportLoading(false);
    }
  };

  exportObservationsRef.current = exportObservationsToExcel;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");
    if (!action) return;
    if (action === "new") {
      setWizardObservationId(null);
      setWizardOpen(true);
      history.replace({ pathname: location.pathname, search: "" });
      return;
    }
    if (action === "export") {
      history.replace({ pathname: location.pathname, search: "" });
      const tid = window.setTimeout(() => {
        exportObservationsRef.current();
      }, 0);
      return () => window.clearTimeout(tid);
    }
  }, [location.search, location.pathname, history]);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <React.Fragment>
      <Head title="Observations HSE" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Observations HSE</BlockTitle>
              <BlockDes className="text-soft">Vous avez {filteredData.length} observation(s) au total</BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="menu-alt-r"></Icon>
                </Button>
                <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <Icon name="search"></Icon>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Rechercher une observation..."
                          value={searchText}
                          onChange={(e) => {
                            setSearchText(e.target.value);
                            setCurrentPage(1); // Reset pagination on search
                          }}
                        />
                      </div>
                    </li>
                    <li>
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="dropdown-toggle btn btn-white btn-dim btn-outline-light">
                          <Icon name="filter-alt" className="d-none d-sm-inline"></Icon>
                          <span>Filtrer par</span>
                          <Icon name="chevron-right" className="dd-indc"></Icon>
                        </DropdownToggle>
                        <DropdownMenu end>
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ type: "", status: "", severity: "" });
                                }}
                              >
                                <span>Tous</span>
                              </DropdownItem>
                            </li>
                            <li className="divider"></li>
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ ...filters, status: "submitted" });
                                }}
                              >
                                <span>Soumises</span>
                              </DropdownItem>
                            </li>
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ ...filters, status: "closed" });
                                }}
                              >
                                <span>Clôturées</span>
                              </DropdownItem>
                            </li>
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ ...filters, type: "dangerous" });
                                }}
                              >
                                <span>Dangereuses</span>
                              </DropdownItem>
                            </li>
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#dropdownitem"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setFilters({ ...filters, severity: "high" });
                                }}
                              >
                                <span>Haute gravité</span>
                              </DropdownItem>
                            </li>
                          </ul>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                    <li className="nk-block-tools-opt">
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="a"
                          href="#actions"
                          className={`dropdown-toggle btn btn-primary ${exportLoading ? "disabled pe-none opacity-75" : ""}`}
                          onClick={(ev) => exportLoading && ev.preventDefault()}
                        >
                          {exportLoading ? (
                            <>
                              <Spinner size="sm" className="me-1 align-middle" />
                              <span>Préparation…</span>
                            </>
                          ) : (
                            <>
                              <span>Actions</span>
                              <Icon name="chevron-down" className="dd-indc"></Icon>
                            </>
                          )}
                        </DropdownToggle>
                        <DropdownMenu end>
                          <ul className="link-list-opt no-bdr">
                            <PermissionGate permission={["observations.create", "Observations - Créer"]}>
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#new-observation"
                                  disabled={exportLoading}
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    if (!exportLoading) openNewObservationWizard();
                                  }}
                                >
                                  <Icon name="plus"></Icon>
                                  <span>Nouvelle observation</span>
                                </DropdownItem>
                              </li>
                              <li className="divider"></li>
                            </PermissionGate>
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#export-excel"
                                disabled={exportLoading}
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  if (!exportLoading) exportObservationsToExcel();
                                }}
                              >
                                {exportLoading ? (
                                  <>
                                    <Spinner size="sm" className="me-1" />
                                    <span>Export en cours…</span>
                                  </>
                                ) : (
                                  <>
                                    <Icon name="download-cloud"></Icon>
                                    <span>Export Excel</span>
                                  </>
                                )}
                              </DropdownItem>
                            </li>
                          </ul>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block className="position-relative">
          {exportLoading && filteredData.length > 0 && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
              style={{
                zIndex: 25,
                backgroundColor: "rgba(255, 255, 255, 0.88)",
                backdropFilter: "blur(2px)",
                minHeight: "280px",
              }}
            >
              <div className="text-center px-3">
                <Spinner color="primary" style={{ width: "2.5rem", height: "2.5rem" }} />
                <p className="mt-3 mb-0 fw-medium">Préparation de l&apos;export Excel</p>
                <p className="text-soft small mt-1 mb-0">
                  Récupération des détails ({filteredData.length} observation{filteredData.length > 1 ? "s" : ""})…
                </p>
              </div>
            </div>
          )}
          {loading ? (
            <div className="card card-stretch">
              <div className="card-inner">
                <div className="text-center py-5">Chargement...</div>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="card card-stretch">
              <div className="card-inner">
                <div className="text-center py-5">
                  <p className="text-soft">Aucune observation trouvée</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <DataTable className="card-stretch">
                <DataTableBody>
                  <DataTableHead className="nk-tb-item nk-tb-head">
                    <DataTableRow className="nk-tb-col-check">
                      <div className="custom-control custom-control-sm custom-checkbox notext">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="obs-all"
                          onChange={(e) => selectorCheck(e)}
                        />
                        <label className="custom-control-label" htmlFor="obs-all"></label>
                      </div>
                    </DataTableRow>
                    <DataTableRow size="lg">
                      <span className="sub-text">Observation</span>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      <span className="sub-text">Type</span>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      <span className="sub-text">Gravité</span>
                    </DataTableRow>
                    <DataTableRow size="xl">
                      <span className="sub-text">Statut</span>
                    </DataTableRow>
                    <DataTableRow size="sm">
                      <span className="sub-text">Date</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end" size="sm">
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="btn btn-xs btn-trigger btn-icon dropdown-toggle me-n1">
                          <Icon name="more-h"></Icon>
                        </DropdownToggle>
                        <DropdownMenu end>
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <DropdownItem
                                tag="a"
                                href="#remove"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                }}
                              >
                                <Icon name="trash"></Icon>
                                <span>Supprimer</span>
                              </DropdownItem>
                            </li>
                          </ul>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </DataTableRow>
                  </DataTableHead>
                  {currentItems.map((item) => (
                    <DataTableItem key={item.id}>
                      <DataTableRow className="nk-tb-col-check">
                        <div className="custom-control custom-control-sm custom-checkbox notext">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            defaultChecked={item.checked}
                            id={item.id + "obs"}
                            key={Math.random()}
                            onChange={(e) => onSelectChange(e, item.id)}
                          />
                          <label className="custom-control-label" htmlFor={item.id + "obs"}></label>
                        </div>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <div style={{ maxWidth: 320 }}>
                          <span
                            className="tb-lead text-primary"
                            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                            onClick={() => onViewClick(item.id)}
                            onKeyDown={(e) => e.key === 'Enter' && onViewClick(item.id)}
                            role="button"
                            tabIndex={0}
                            title={item.title || "Sans titre"}
                          >
                            {item.title || "Sans titre"}
                          </span>
                          {(item.source_inspection_id || item.source_inspection?.id) && (
                            <a
                              className="tb-sub small d-block mt-1 link"
                              href={`${process.env.PUBLIC_URL || ""}/hse-process/inspections/${item.source_inspection_id || item.source_inspection?.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Icon name="link-alt" className="me-1" />
                              Inspection #{item.source_inspection_id || item.source_inspection?.id}
                              {item.source_inspection?.title ? ` — ${item.source_inspection.title}` : ""}
                            </a>
                          )}
                          {(item.derived_incidents_count ?? 0) > 0 && (
                            <a
                              className="tb-sub small d-block mt-1 link"
                              href={`${process.env.PUBLIC_URL || ""}/hse-process/incidents?linked_observation=${item.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Icon name="alert-circle" className="me-1" />
                              {item.derived_incidents_count} incident
                              {item.derived_incidents_count > 1 ? "s" : ""} HSE lié
                              {item.derived_incidents_count > 1 ? "s" : ""}
                            </a>
                          )}
                        </div>
                      </DataTableRow>
                      <DataTableRow size="xl">
                        {item.type ? (
                          <Badge color={item.type === 'positive' ? 'success' : item.type === 'dangerous' ? 'danger' : item.type === 'near_miss' ? 'warning' : 'info'}>
                            {item.type === 'positive' ? 'Positive' : item.type === 'dangerous' ? 'Dangereuse' : item.type === 'near_miss' ? "Presqu'accident" : item.type === 'non_conformity' ? 'Non-conformité' : item.type}
                          </Badge>
                        ) : (
                          <span className="badge badge-dim bg-outline-secondary">-</span>
                        )}
                      </DataTableRow>
                      <DataTableRow size="xl">
                        {item.severity ? (
                          <Badge color={item.severity === 'low' ? 'success' : item.severity === 'medium' ? 'warning' : 'danger'}>
                            {item.severity === 'low' ? 'Faible' : item.severity === 'medium' ? 'Moyenne' : item.severity === 'high' ? 'Élevée' : item.severity}
                          </Badge>
                        ) : (
                          <span className="badge badge-dim bg-outline-secondary">-</span>
                        )}
                      </DataTableRow>
                      <DataTableRow size="xl">
                        {item.status ? (
                          <Badge color={item.status === 'submitted' ? 'secondary' : item.status === 'received' ? 'info' : item.status === 'in_analysis' ? 'warning' : item.status === 'corrective_action_requested' ? 'danger' : item.status === 'action_completed' ? 'success' : item.status === 'verified' ? 'primary' : 'dark'}>
                            {item.status === 'submitted' ? 'Soumise' : item.status === 'received' ? 'Reçue' : item.status === 'in_analysis' ? 'En analyse' : item.status === 'corrective_action_requested' ? 'Action requise' : item.status === 'action_completed' ? 'Action réalisée' : item.status === 'verified' ? 'Vérifiée' : item.status === 'closed' ? 'Clôturée' : item.status}
                          </Badge>
                        ) : (
                          <span className="badge badge-dim bg-outline-secondary">-</span>
                        )}
                      </DataTableRow>
                      <DataTableRow size="sm">
                        <span className="tb-date" style={{ whiteSpace: 'nowrap' }}>
                          {new Date(item.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools" size="sm">
                        <UncontrolledDropdown>
                          <DropdownToggle tag="a" className="btn btn-sm btn-icon btn-trigger">
                            <Icon name="more-h"></Icon>
                          </DropdownToggle>
                          <DropdownMenu end>
                            <ul className="link-list-opt no-bdr">
                              <li>
                                <DropdownItem
                                  tag="a"
                                  href="#view"
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    onViewClick(item.id);
                                  }}
                                >
                                  <Icon name="eye"></Icon>
                                  <span>Voir</span>
                                </DropdownItem>
                              </li>
                              <PermissionGate permission={["Incidents - Créer"]}>
                                <li>
                                  <DropdownItem
                                    tag="a"
                                    href="#incident"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      history.push(
                                        `${process.env.PUBLIC_URL || ""}/hse-process/incidents?from_observation=${item.id}`
                                      );
                                    }}
                                  >
                                    <Icon name="alert-circle"></Icon>
                                    <span>Créer un incident lié</span>
                                  </DropdownItem>
                                </li>
                              </PermissionGate>
                              <PermissionGate permission={["observations.edit", "observations.update", "Observations - Modifier"]}>
                                <li>
                                  <DropdownItem
                                    tag="a"
                                    href="#edit"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      onEditClick(item.id);
                                    }}
                                  >
                                    <Icon name="edit"></Icon>
                                    <span>Modifier</span>
                                  </DropdownItem>
                                </li>
                              </PermissionGate>
                              <PermissionGate permission={["observations.delete", "Observations - Supprimer"]}>
                                <li className="divider"></li>
                                <li>
                                  <DropdownItem
                                    tag="a"
                                    href="#delete"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      onDeleteClick(item.id);
                                    }}
                                    className="text-danger"
                                  >
                                    <Icon name="trash"></Icon>
                                    <span>Supprimer</span>
                                  </DropdownItem>
                                </li>
                              </PermissionGate>
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
                </DataTableBody>
              </DataTable>
              {filteredData.length > 0 && (
                <div className="card-inner">
                  <PaginationComponent
                    itemPerPage={itemPerPage}
                    totalItems={filteredData.length}
                    paginate={paginate}
                    currentPage={currentPage}
                  />
                </div>
              )}
            </>
          )}
        </Block>


        <ObservationWizardModal
          isOpen={wizardOpen}
          onClose={closeWizard}
          observationId={wizardObservationId}
          onSuccess={onWizardSuccess}
        />

        <ObservationViewModal
          isOpen={viewModalOpen}
          onClose={closeViewModal}
          observationId={viewObservationId}
          onEditClick={(id) => {
            closeViewModal();
            setWizardObservationId(id);
            setWizardOpen(true);
          }}
        />

        {/* Modal de confirmation de suppression */}
        <Modal isOpen={deleteModal} toggle={onDeleteCancel} className="modal-dialog-centered">
          <div className="modal-header">
            <h5 className="modal-title">Confirmer la suppression</h5>
            <button className="close" onClick={onDeleteCancel}>
              <Icon name="cross"></Icon>
            </button>
          </div>
          <ModalBody>
            <p>Êtes-vous sûr de vouloir supprimer l'observation <strong>"{deleteTitle}"</strong> ?</p>
            <p className="text-soft small">Cette action est irréversible.</p>
            <div className="form-group text-end mt-4">
              <Button color="light" onClick={onDeleteCancel} className="me-2">
                Annuler
              </Button>
              <Button color="danger" onClick={onDeleteConfirm}>
                Supprimer
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default ObservationListPage;
